// Editing state for the Profile page (step 9 of the implementation plan).
//
// One reducer, one React context. The reducer holds:
//
//   - `mode`            — view vs edit
//   - `draft`           — partial patch object (reset on Cancel, sent on Save)
//   - `fieldErrors`     — per-field validation messages, set by the server's
//                         400 response and cleared on the next Save attempt
//   - `formError`       — top-level toast string (e.g. "Please fix...")
//
// Components subscribe via `useProfileEditing()`.

import { createContext, useContext } from 'react';
import type {
  EmployeeProfile,
  ProfileUpdateRequest,
  ProfileGetResponse,
} from '@pa/shared';

export type ProfileEditMode = 'view' | 'edit';

export interface ProfileEditState {
  mode: ProfileEditMode;
  draft: ProfileUpdateRequest;
  fieldErrors: Record<string, string[]>;
  formError: string | null;
  saving: boolean;
}

export type ProfileEditAction =
  | { type: 'START_EDIT'; snapshot: EmployeeProfile }
  | { type: 'SET_FIELD'; key: keyof ProfileUpdateRequest; value: unknown }
  | { type: 'CANCEL' }
  | { type: 'SAVING' }
  | { type: 'SAVE_FAILED'; fieldErrors: Record<string, string[]>; formError: string }
  | { type: 'SAVE_SUCCEEDED' };

export const initialEditState: ProfileEditState = {
  mode: 'view',
  draft: {},
  fieldErrors: {},
  formError: null,
  saving: false,
};

export function profileEditReducer(
  state: ProfileEditState,
  action: ProfileEditAction,
): ProfileEditState {
  switch (action.type) {
    case 'START_EDIT':
      return { mode: 'edit', draft: {}, fieldErrors: {}, formError: null, saving: false };
    case 'SET_FIELD':
      return {
        ...state,
        draft: { ...state.draft, [action.key]: action.value },
        // Clear the error for that field as the user edits.
        fieldErrors: stripError(state.fieldErrors, action.key as string),
      };
    case 'CANCEL':
      return initialEditState;
    case 'SAVING':
      return { ...state, saving: true, formError: null };
    case 'SAVE_FAILED':
      return {
        ...state,
        saving: false,
        fieldErrors: action.fieldErrors,
        formError: action.formError,
      };
    case 'SAVE_SUCCEEDED':
      return initialEditState;
  }
}

function stripError(errors: Record<string, string[]>, key: string): Record<string, string[]> {
  if (!(key in errors)) return errors;
  const next = { ...errors };
  delete next[key];
  return next;
}

// Map a ProfileGetResponse error body to fieldErrors + formError. The server
// emits `{ error, message, issues: { fieldErrors } }` on validation failure
// and `{ error: 'forbidden_fields', fields: [...] }` on permission failure.
export function parseSaveError(body: unknown): {
  fieldErrors: Record<string, string[]>;
  formError: string;
} {
  if (typeof body === 'object' && body !== null) {
    const b = body as Record<string, unknown>;
    if (b.error === 'validation_error' && typeof b.message === 'string') {
      const issues = (b.issues as { fieldErrors?: Record<string, string[]> } | undefined) ?? {};
      return {
        fieldErrors: issues.fieldErrors ?? {},
        formError: b.message,
      };
    }
    if (b.error === 'forbidden_fields' && Array.isArray(b.fields)) {
      const fieldErrors: Record<string, string[]> = {};
      for (const f of b.fields as string[]) {
        fieldErrors[f] = ['You do not have permission to edit this field'];
      }
      return { fieldErrors, formError: 'Some fields are not editable for your role' };
    }
  }
  return { fieldErrors: {}, formError: 'Save failed. Please try again.' };
}

// Look up the section a field belongs to so the sub-sidebar can show a red
// dot beside the section that contains an error.
export const FIELD_TO_SECTION: Record<string, string> = {
  firstName: 'overview',
  lastName: 'overview',
  middleName: 'overview',
  displayName: 'overview',
  workEmail: 'overview',
  employeeIdCode: 'overview',
  startDate: 'overview',
  siteId: 'overview',
  approverTimeOffEmployeeId: 'overview',
  teamLeadEmployeeId: 'overview',
  functionalLeadEmployeeId: 'overview',
  jobTitle: 'overview',
  teamId: 'overview',
  studioId: 'overview',
  dateOfBirth: 'personalData',
  legalGender: 'personalData',
  pronouns: 'personalData',
  personalEmail: 'personalData',
  personalMobileCountry: 'personalData',
  personalMobileNumber: 'personalData',
  emergencyContactName: 'personalData',
  emergencyContactCountry: 'personalData',
  emergencyContactNumber: 'personalData',
  addressStreet: 'address',
  addressCityZip: 'address',
  addressCountry: 'address',
  passportNumber: 'identification',
  cpr: 'identification',
  passportExpirationDate: 'identification',
  someConsent: 'someConsent',
  functionId: 'work',
  contractType: 'work',
  workingHours: 'work',
  additionalPensionContribution: 'payroll',
  employeePensionContribution: 'payroll',
  baseSalary: 'payroll',
  salaryType: 'payroll',
  salaryPayFrequency: 'payroll',
  currency: 'payroll',
  visaRequired: 'workingVisa',
  visaApplicationDate: 'workingVisa',
  visaValidFrom: 'workingVisa',
  visaExpirationDate: 'workingVisa',
  discoCode: 'workingVisa',
  seniorityLevel: 'tcField',
  advancementLevel: 'tcField',
  cheqroomLink: 'itHardware',
  hardwareNotes: 'itHardware',
  lastDayAtWork: 'termination',
  lastContractualDay: 'termination',
};

export function sectionsWithErrors(fieldErrors: Record<string, string[]>): Set<string> {
  const sections = new Set<string>();
  for (const key of Object.keys(fieldErrors)) {
    const section = FIELD_TO_SECTION[key];
    if (section) sections.add(section);
  }
  return sections;
}

// ── Sub-sidebar search (T-015, spec §6a) ─────────────────────────────
//
// The Profile page's left sub-sidebar gets a "Search fields" input. Typing
// filters the section list to entries whose **title** or **any field label
// inside that section** matches the query. This is the data the filter
// runs against.
//
// Map is grouped per section in the same order as profile-page-spec.md §7
// so a future refactor that adds a field can drop the new label into the
// matching bucket without touching the filter logic. Source labels match
// the human-readable strings rendered in `<EditableField label="…">` and
// `<StaticField label="…">` calls in `ProfileView.tsx`.

export const SECTION_FIELD_LABELS: Record<string, string[]> = {
  overview: [
    'Display name',
    'First name',
    'Last name',
    'Middle name',
    'Work email',
    'Employee ID',
    'Start date',
    'Site',
    'Approver (time off)',
    'Team lead',
    'Functional lead',
    'Job title',
    'Team',
    'Studio',
  ],
  personalData: [
    'Date of birth',
    'Legal gender',
    'Pronouns',
    'Personal email',
    'Personal mobile',
    'Emergency contact',
  ],
  address: ['Street', 'City / ZIP', 'Country'],
  identification: ['Passport number', 'CPR', 'Passport expiration'],
  someConsent: ['Social media consent'],
  work: ['Function', 'Contract type', 'Working hours', 'Tenure'],
  payroll: [
    'Additional pension',
    'Employee pension',
    'Base salary',
    'Salary type',
    'Salary frequency',
    'Currency',
  ],
  workingVisa: [
    'Visa required',
    'Application date',
    'Valid from',
    'Expiration date',
    'DISCO code',
  ],
  tcField: ['Seniority level', 'Advancement level'],
  itHardware: ['Cheqroom link', 'Hardware notes'],
  termination: ['Last day at work', 'Last contractual day', 'Notice period'],
};

/**
 * Filter the section list against a free-text search query, matching either
 * the section's display title or any of its field labels (case-insensitive,
 * substring). Empty / whitespace query returns the full ordered list.
 *
 * @param sections - Section keys in the order they should render. Caller
 *   typically passes `permissions.visibleSections` already filtered to
 *   what the viewer can see.
 * @param query - The text the user typed in the search input.
 * @param sectionLabels - Map of section key → display title (for matching).
 */
export function filterSectionsByQuery(
  sections: string[],
  query: string,
  sectionLabels: Record<string, string>,
): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return sections;
  return sections.filter((key) => {
    const title = (sectionLabels[key] ?? '').toLowerCase();
    if (title.includes(q)) return true;
    const fields = SECTION_FIELD_LABELS[key] ?? [];
    return fields.some((label) => label.toLowerCase().includes(q));
  });
}

// Admin-config option lists, fetched lazily when an admin enters edit mode.
// Each entry is the same shape regardless of list (sites/teams/studios/
// functions): id + display label + archived flag (so the UI can disable
// archived options while still rendering them when an existing record
// already references them).
export interface AdminConfigOption {
  id: string;
  label: string;
  archived: boolean;
}

export interface AdminConfigLists {
  sites: AdminConfigOption[];
  teams: AdminConfigOption[];
  studios: AdminConfigOption[];
  functions: AdminConfigOption[];
}

// Context that the EditableField components read.
export interface ProfileEditingContextValue {
  state: ProfileEditState;
  dispatch: (action: ProfileEditAction) => void;
  profile: EmployeeProfile | null;
  canEdit: Set<string>;
  // Available when an admin enters edit mode. `null` for non-admin viewers
  // (FK fields aren't editable for them anyway) or before the lists load.
  adminConfig: AdminConfigLists | null;
}

export const ProfileEditingContext = createContext<ProfileEditingContextValue | null>(null);

export function useProfileEditing(): ProfileEditingContextValue {
  const ctx = useContext(ProfileEditingContext);
  if (!ctx) throw new Error('useProfileEditing must be inside <ProfileEditingProvider>');
  return ctx;
}

// Resolve the *current* value the UI should show for a given field — either
// from the draft (when present) or from the underlying profile.
export function currentValue<K extends keyof ProfileUpdateRequest>(
  state: ProfileEditState,
  profile: EmployeeProfile | null,
  key: K,
): ProfileUpdateRequest[K] | EmployeeProfile[Extract<K, keyof EmployeeProfile>] | null | undefined {
  if (state.mode === 'edit' && key in state.draft) {
    return (state.draft as Record<string, unknown>)[key as string] as ProfileUpdateRequest[K];
  }
  if (profile) {
    return (profile as unknown as Record<string, unknown>)[key as string] as never;
  }
  return null;
}

export type { ProfileGetResponse };

// ── Profile tab resolution (T-325) ────────────────────────────────────
//
// The profile tab is selected by the `?tab=` query parameter, and the
// server produces links carrying it (a time-off notification points at
// the Time off tab). Those two spellings drifted: `ProfileView`'s key is
// `timeOff`, and every server-produced link wrote `tab=timeoff`, so the
// links silently opened Profile Overview instead. The server now writes
// the canonical key, and this resolver keeps the links already sitting in
// Slack messages and inboxes working by matching case-insensitively.
//
// Keep `PROFILE_TAB_KEYS` in step with `TABS` in ProfileView.

export const PROFILE_TAB_KEYS = [
  'overview',
  'timeOff',
  'attendance',
  'tasks',
  'docs',
  'contract',
  'reviews',
  'notifications',
] as const;

export type ProfileTabKey = (typeof PROFILE_TAB_KEYS)[number];

/**
 * The tab a `?tab=` value selects. Unknown values fall back to
 * `overview`, which is what the render already did for them.
 */
export function resolveProfileTab(param: string | null | undefined): ProfileTabKey {
  if (!param) return 'overview';
  const needle = param.trim().toLowerCase();
  return (
    PROFILE_TAB_KEYS.find((key) => key.toLowerCase() === needle) ?? 'overview'
  );
}
